import {
    TableCell, tableCellClasses, TableRow, TextField, styled
} from "@mui/material";
import CheckAttr from "../../../utils/CheckAttr";
import { FC, useEffect, useState } from "react";
import { ActionType } from "../../../types/AlertType";
import { Heure, DataArticle } from "./AddSaisieAgentForm";
import { checkViaCodeArticle } from "../../../utils/utils";
import { articlesSansActes, codeArticleNotToDisable } from "../../../constants/Constants";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        fontSize: 11,
        textAlign: 'center'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 11,
        textAlign: 'center'
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0
    },
}));

interface AddCoutRowFormProps {
    article: DataArticle;
    handleChangeSaisieManager: (heure: Heure, action: ActionType) => void;
};

const AddSaisieAgentRowForm: FC<AddCoutRowFormProps> = ({ article, handleChangeSaisieManager }) => {
    const tarification = article?.tarification && article?.tarification;
    const coutParHeure = article?.EstFormation ? article?.cout?.coutformation : article?.cout?.coutactivite;
    const saisieManager = article?.saisie_manager && article?.saisie_manager[0];

    // Heures de management non facturées : actes toujours à 0
    const sansActes = articlesSansActes.includes(article?.libelle);

    const tarifHeure = tarification ? Number(tarification?.tarifheure) : 0;
    const tarifActe = tarification ? Number(tarification?.tarifacte) : 0;
    const heureCA = saisieManager?.heures_ca ? Number(saisieManager?.heures_ca) : 0;
    const acteCA = saisieManager?.actes ? Number(saisieManager?.actes) : 0;

    const initialHeureAgent: Heure = {
        article: article.id,
        caHeures: saisieManager && saisieManager?.heures_ca,
        caActes: (!saisieManager && sansActes) ? "0" : (saisieManager && saisieManager?.actes),
        tarifHeures: tarifHeure,
        tarifActes: tarifActe,
        chiffreAffaire: (tarifHeure * heureCA) + (tarifActe * acteCA),
        coutHeures: saisieManager && saisieManager?.heuresprod,
        dmt: saisieManager && saisieManager?.dmt,
        coutParHeures: coutParHeure ? Number(coutParHeure) : 0,
        cout: (saisieManager?.heuresprod && coutParHeure) ? (Number(saisieManager.heuresprod) * Number(coutParHeure)) : undefined,
        manual: saisieManager && saisieManager?.manual
    };

    const [heuresToSave, setHeuresToSave] = useState<Heure>(initialHeureAgent);

    useEffect(() => {
        handleChangeSaisieManager(initialHeureAgent, 'CREATE');
    }, []);

    const calculCA = (name: string, value: any) => {
        let ChifrAff = 0;
        if (name === 'caHeures') {
            const CaHeure = value ? Number(value) : 0;
            const tarifHeure = tarification?.tarifheure ? Number(tarification?.tarifheure) : 0;
            const CaActe = heuresToSave?.caActes ? Number(heuresToSave?.caActes) : 0;
            const tarifActe = tarification?.tarifacte ? Number(tarification?.tarifacte) : 0;

            ChifrAff = (CaHeure * tarifHeure) + (CaActe * tarifActe);
        }

        if (name === 'caActes') {
            const CaHeure = heuresToSave?.caHeures ? Number(heuresToSave?.caHeures) : 0;
            const tarifHeure = tarification?.tarifheure ? Number(tarification?.tarifheure) : 0;
            const CaActe = value ? Number(value) : 0;
            const tarifActe = tarification?.tarifacte ? Number(tarification?.tarifacte) : 0;

            ChifrAff = (CaHeure * tarifHeure) + (CaActe * tarifActe);
        }
        return ChifrAff;
    }

    const calculCout = (value: any, coutParHeure: any) => {
        const prodParHeure = value ? Number(value) : 0;
        const coutPerHour = coutParHeure ? Number(coutParHeure) : 0;

        return prodParHeure * coutPerHour;
    }

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        let updatedHeure: Heure;

        const val = value.replace(",", ".");

        switch (name) {
            case 'caHeures':
                updatedHeure = {
                    ...heuresToSave,
                    caHeures: val,
                    chiffreAffaire: calculCA(name, val)
                };
                break;
            case 'caActes':
                updatedHeure = {
                    ...heuresToSave,
                    caActes: val,
                    chiffreAffaire: calculCA(name, val)
                };
                break;
            case 'coutHeures':
                updatedHeure = {
                    ...heuresToSave,
                    coutHeures: val,
                    cout: calculCout(val, coutParHeure)
                };
                break;
            default:
                updatedHeure = {
                    ...heuresToSave,
                    [name]: val
                };
                break;
        }

        setHeuresToSave(updatedHeure);
        handleChangeSaisieManager(updatedHeure, 'UPDATE');
    };

    const disableHeureInput = (): boolean => {
        if (CheckAttr(article?.saisie_manager) ||
            (!sansActes && CheckAttr(heuresToSave?.caActes)) ||
            checkViaCodeArticle(codeArticleNotToDisable, ['A', 'a'], article?.code_article?.libelle)
            || !CheckAttr(tarification?.tarifheure)
        ) {
            return true;
        }

        return false;
    }

    const disableActeInput = (): boolean => {
        if (sansActes ||
            CheckAttr(article?.saisie_manager) ||
            CheckAttr(heuresToSave?.caHeures) ||
            checkViaCodeArticle(codeArticleNotToDisable, ['H', 'h'], article?.code_article?.libelle)
            || !CheckAttr(tarification?.tarifacte)
        ) {
            return true;
        }

        return false;
    }

    let decimalPrecision = 2;
    if (article?.devise?.code_iso === 'TND') {
        decimalPrecision = 3;
    }

    return (
        <StyledTableRow>
            <StyledTableCell>
                {article?.code_article?.libelle}
            </StyledTableCell>
            <StyledTableCell align="center">
                {article?.libelle}
            </StyledTableCell>
            <StyledTableCell>
                <TextField
                    name="caHeures"
                    color="warning"
                    variant="filled"
                    size="small"
                    // type="number"
                    autoComplete="off"
                    placeholder="Ex: 2.02"
                    /* inputProps={{
                        step: 0.01,
                        lang: 'en-US'
                    }} */
                    sx={{ width: 105 }}
                    value={heuresToSave?.caHeures}
                    disabled={disableHeureInput()}
                    onChange={handleChange}
                    InputLabelProps={{
                        shrink: CheckAttr(heuresToSave?.caHeures),
                    }}
                />
            </StyledTableCell>

            <StyledTableCell>
                <TextField
                    name="caActes"
                    color="warning"
                    variant="filled"
                    size="small"
                    // type="number"
                    autoComplete="off"
                    placeholder="Ex: 2.02"
                    /* inputProps={{
                        step: 0.01,
                        lang: 'en-US'
                    }} */
                    sx={{ width: 105 }}
                    value={heuresToSave?.caActes}
                    disabled={disableActeInput()}
                    onChange={handleChange}
                    InputLabelProps={{
                        shrink: CheckAttr(heuresToSave?.caActes)
                    }}
                />
            </StyledTableCell>

            <StyledTableCell>
                <div style={{ width: 50 }}>
                    {tarification?.tarifheure && parseFloat(tarification?.tarifheure)?.toFixed(decimalPrecision)}
                </div>
            </StyledTableCell>

            <StyledTableCell>
                {tarification?.tarifacte && parseFloat(tarification?.tarifacte)?.toFixed(decimalPrecision)}
            </StyledTableCell>

            <StyledTableCell>
                {heuresToSave?.chiffreAffaire && parseFloat(String(heuresToSave?.chiffreAffaire))?.toFixed(decimalPrecision)}
            </StyledTableCell>

            <StyledTableCell />

            <StyledTableCell>
                <TextField
                    name="coutHeures"
                    color="warning"
                    variant="filled"
                    size="small"
                    // type="number"
                    sx={{ width: 75 }}
                    autoComplete="off"
                    placeholder="Ex: 2.02"
                    /* inputProps={{
                        step: 0.01,
                        lang: 'en-US'
                    }} */
                    value={heuresToSave?.coutHeures}
                    disabled={CheckAttr(article?.saisie_manager)}
                    onChange={handleChange}
                    InputLabelProps={{
                        shrink: CheckAttr(heuresToSave?.coutHeures)
                    }}
                />
            </StyledTableCell>
            <StyledTableCell>
                {coutParHeure && parseFloat(coutParHeure)?.toFixed(decimalPrecision)}
            </StyledTableCell>

            <StyledTableCell>
                {heuresToSave?.cout && parseFloat(String(heuresToSave?.cout))?.toFixed(decimalPrecision)}
            </StyledTableCell>

            <StyledTableCell>
                {article?.devise?.code_iso}
            </StyledTableCell>

            <StyledTableCell />
            <StyledTableCell />
        </StyledTableRow>
    );
};

export default AddSaisieAgentRowForm;